type RoofSurfacesExtendedResult = Record<
  'coverageRSExtended' | 'stateSpecificLockField' | 'stateOverride' | 'roofAgeLimit',
  any
>;

// Confirmed TX & OK underwriting rules (github issue #35453). In TX and OK an insured may select
// Roof Surfaces Extended Coverage ONLY in these scenarios; every other wind/hail deductible x
// roof-age combination (including flat-dollar deductibles and any other percentage) is Standard-only:
//
//   | wind/hail deductible | roof age eligible for Extended |
//   | 1% (0.01)            | 0-3 years                      |
//   | 2% (0.02)            | 0-6 years                      |
//   | 5% (0.05)            | 7+ years                       |
//
// Roof age is measured against the rate-control date everywhere (rater + quote-fe). Note the 5%
// rule is a MINIMUM-age gate, so eligibility cannot be expressed as a single "max roof age" — callers
// that need the yes/no answer must use `coverageRSExtended` / `stateSpecificLockField` (or
// willHaveCoverageRSExtended), not `roofAgeLimit` alone.
const isRoofSurfacesExtendedEligible = (roofAge: number, deductibleWindHail: number): boolean =>
  (deductibleWindHail === 0.01 && roofAge <= 3) ||
  (deductibleWindHail === 0.02 && roofAge <= 6) ||
  (deductibleWindHail === 0.05 && roofAge >= 7);

// The maximum roof age at which Extended remains available for a given deductible, kept for the
// legacy `roofAge > roofAgeLimit` display/renewal paths. 5% has no upper bound (it becomes eligible
// at 7+ and never ages out), so it reports 99; deductibles that never qualify report 0.
const roofAgeLimitForDeductible = (deductibleWindHail: number): number => {
  if (deductibleWindHail === 0.01) {
    return 3;
  }
  if (deductibleWindHail === 0.02) {
    return 6;
  }
  if (deductibleWindHail === 0.05) {
    return 99;
  }
  return 0;
};

const getRoofSurfacesExtendedForTXOK = (
  roofAge: number,
  deductibleWindHail: number,
  coverageRSExtended?: boolean
): RoofSurfacesExtendedResult => {
  const roofAgeLimit = roofAgeLimitForDeductible(deductibleWindHail);

  if (!isRoofSurfacesExtendedEligible(roofAge, deductibleWindHail)) {
    // Not eligible: force (lock to) Standard regardless of what the member selected.
    return { coverageRSExtended: false, stateSpecificLockField: true, stateOverride: true, roofAgeLimit };
  }

  // Eligible: the member may choose; honor their current selection.
  return {
    coverageRSExtended: !!coverageRSExtended,
    stateSpecificLockField: false,
    stateOverride: true,
    roofAgeLimit
  };
};

export const getTXRoofSurfacesExtended = (
  roofAge: number,
  deductibleWindHail: number,
  coverageRSExtended?: boolean
): RoofSurfacesExtendedResult => getRoofSurfacesExtendedForTXOK(roofAge, deductibleWindHail, coverageRSExtended);

// TX and OK share the same confirmed rules (github issue #35453).
export const getOKRoofSurfacesExtended = (
  roofAge: number,
  deductibleWindHail: number,
  coverageRSExtended?: boolean
): RoofSurfacesExtendedResult => getRoofSurfacesExtendedForTXOK(roofAge, deductibleWindHail, coverageRSExtended);
